import React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

interface CTAButtonProps {
  text?: string;
  href: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  showArrow?: boolean;
  external?: boolean;
  children?: React.ReactNode;
}

export function CTAButton({
  text,
  href,
  variant = 'primary',
  size = 'md',
  showArrow = true,
  external = false,
  children,
}: CTAButtonProps) {
  const baseStyles = 'inline-flex items-center gap-2 font-semibold rounded-lg transition-all hover:scale-105 active:scale-95';

  const variantStyles = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  const className = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]}`;

  const label = children ?? text;

  const content = (
    <>
      <span>{label}</span>
      {showArrow && <ArrowRight className="h-4 w-4" />}
    </>
  );

  if (external) {
    return (
      <div className="my-6 flex justify-center">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </a>
      </div>
    );
  }

  return (
    <div className="my-6 flex justify-center">
      <Link
        href={href}
        className={className}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </Link>
    </div>
  );
}
